import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { resolveLocalUrl } from '../utils/mediaUtils';

const _hexToRgba = (hex: string, alpha: number): string | null => {
    let c = hex.replace('#', '');
    if (c.length === 3) c = c[0]+c[0]+c[1]+c[1]+c[2]+c[2];
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
    return `rgba(${r},${g},${b},${alpha})`;
};

interface IconProps extends React.SVGProps<SVGSVGElement> {
    isLiked?: boolean;
    isVouched?: boolean;
    isBumped?: boolean;
    isBookmarked?: boolean;
}

// ----------------------------------------------------
// DYNAMIC ICON WRAPPER HOOK & COMPILER
// ----------------------------------------------------
const getBrightness = (color: string): number => {
    let r = 255, g = 255, b = 255;
    try {
        const cleanColor = color.trim().toLowerCase();
        if (cleanColor.startsWith('#')) {
            let hex = cleanColor.replace('#', '');
            if (hex.length === 3) {
                hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
            } else if (hex.length < 6) {
                hex = hex.padEnd(6, '0');
            } else if (hex.length > 6) {
                hex = hex.substring(0, 6);
            }
            const parsedR = parseInt(hex.substring(0, 2), 16);
            const parsedG = parseInt(hex.substring(2, 4), 16);
            const parsedB = parseInt(hex.substring(4, 6), 16);
            if (!isNaN(parsedR)) r = parsedR;
            if (!isNaN(parsedG)) g = parsedG;
            if (!isNaN(parsedB)) b = parsedB;
        } else if (cleanColor.startsWith('rgb')) {
            const matches = cleanColor.match(/\d+/g);
            if (matches && matches.length >= 3) {
                const parsedR = parseInt(matches[0]);
                const parsedG = parseInt(matches[1]);
                const parsedB = parseInt(matches[2]);
                if (!isNaN(parsedR)) r = parsedR;
                if (!isNaN(parsedG)) g = parsedG;
                if (!isNaN(parsedB)) b = parsedB;
            }
        } else if (cleanColor === 'black' || cleanColor === 'transparent') {
            r = 0; g = 0; b = 0;
        }
    } catch (e) {}
    return Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));
};

function createDynamicIcon(iconKey: string, DefaultComponent: React.ComponentType<any>) {
    return (props: any) => {
        let auth;
        try { auth = useAuth(); } catch (e) {}

        const currentUserId = auth?.currentUser;
        const themeColor = (currentUserId && auth?.usersData?.[currentUserId]?.appThemeColor) || null;
        const outlineRgba = themeColor ? _hexToRgba(themeColor, 0.65) : null;
        const stickerOutlineStyle = outlineRgba ? { '--sticker-outline-color': outlineRgba } as React.CSSProperties : {};

        let finalShadowColor = null;
        try {
            let iconColor = props.style?.color || props.color;
            if (!iconColor && props.className) {
                const className = props.className;
                if (className.includes('text-white') || className.includes('text-gray-') || className.includes('text-blue-') || className.includes('text-amber-') || className.includes('text-yellow-') || className.includes('text-red-') || className.includes('text-green-') || className.includes('text-pink-') || className.includes('text-purple-')) {
                    iconColor = '#ffffff';
                } else if (className.includes('text-black')) {
                    iconColor = '#000000';
                }
            }
            if (iconColor) {
                finalShadowColor = getBrightness(iconColor) < 127.5 ? '#ffffff' : '#000000';
            }
        } catch (e) {}

        const shadowStyle = finalShadowColor ? { '--sticker-outline-shadow-color': finalShadowColor } as React.CSSProperties : {};

        const appIcons = auth?.usersData?.['goodlyfe']?.appIcons || {};
        const iconConfig = appIcons[iconKey];

        if (iconConfig) {
            const className = props.className || 'w-6 h-6';

            if (iconConfig.type === 'text') {
                const textVal = iconConfig.text || '';
                return (
                    <span
                        className={`${className} flex items-center justify-center select-none font-black text-center`}
                        style={{
                            fontSize: textVal.length > 2 ? '9px' : '12px',
                            lineHeight: 1,
                            letterSpacing: '-0.03em',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            ...props.style
                        }}
                        onClick={props.onClick}
                    >
                        {textVal.substring(0, 4)}
                    </span>
                );
            }

            if (iconConfig.type === 'media') {
                const mediaUrl = resolveLocalUrl(iconConfig.url || '');
                const isVideo = iconConfig.mediaType === 'video' || mediaUrl.match(/\.(mp4|webm|ogg|mov)(?:\?|$)/i);
                
                const { isNavIcon, isSelected, ...restProps } = props;

                const mediaClassName = isNavIcon
                    ? `relative w-7 h-7 sm:w-8 sm:h-8 rounded-full overflow-hidden transition-all duration-300 object-cover ${isSelected ? 'shadow-[0_0_15px_rgba(255,255,255,0.15)] ring-1 ring-white/20 scale-110 z-10' : 'opacity-40 grayscale hover:opacity-100 hover:grayscale-0'}`
                    : `${className} object-contain rounded-md`;

                const styleProp = !isNavIcon ? { display: 'inline-block', verticalAlign: 'middle', ...props.style } : { ...props.style };

                if (isVideo) {
                    return (
                        <video
                            src={mediaUrl}
                            className={mediaClassName}
                            style={styleProp}
                            autoPlay
                            loop
                            muted
                            playsInline
                            onClick={props.onClick}
                        />
                    );
                } else {
                    return (
                        <img
                            src={mediaUrl}
                            className={mediaClassName}
                            style={styleProp}
                            alt={iconKey}
                            onClick={props.onClick}
                        />
                    );
                }
            }

            if (iconConfig.type === 'svg') {
                let innerHtml = iconConfig.svgContent || '';
                let viewBox = iconConfig.viewBox || "0 0 24 24";
                let fillVal = iconConfig.fill || "none";
                let strokeVal = iconConfig.stroke || "currentColor";
                let strokeWidthVal = iconConfig.strokeWidth || "2";

                // If they pasted the entire <svg> tag, parse it
                if (innerHtml.trim().startsWith('<svg')) {
                    const matchViewBox = innerHtml.match(/viewBox=["']([^"']+)["']/);
                    if (matchViewBox) viewBox = matchViewBox[1];

                    const matchFill = innerHtml.match(/fill=["']([^"']+)["']/);
                    if (matchFill) fillVal = matchFill[1];

                    const matchStroke = innerHtml.match(/stroke=["']([^"']+)["']/);
                    if (matchStroke) strokeVal = matchStroke[1];

                    const matchStrokeWidth = innerHtml.match(/stroke-width=["']([^"']+)["']/);
                    if (matchStrokeWidth) strokeWidthVal = matchStrokeWidth[1];

                    // Extract inner HTML
                    const innerMatch = innerHtml.match(/<svg[^>]*>([\s\S]*)<\/svg>/i);
                    if (innerMatch) {
                        innerHtml = innerMatch[1];
                    }
                }

                const isActive = props.isLiked || props.isBookmarked || props.isBumped || props.isVouched;
                // Allow dynamic colors for active/inactive states
                if (isActive) {
                    if (iconConfig.activeFill) fillVal = iconConfig.activeFill;
                    else if (iconKey.toLowerCase().includes('like') || iconKey === 'LikeIcon') fillVal = '#ff4554';
                    else if (iconKey.toLowerCase().includes('bookmark') || iconKey === 'BookmarkIcon') fillVal = '#3b82f6';
                    else if (iconKey.toLowerCase().includes('bump') || iconKey === 'BumpIcon') fillVal = '#10b981';

                    if (iconConfig.activeStroke) strokeVal = iconConfig.activeStroke;
                    else if (iconKey.toLowerCase().includes('like') || iconKey === 'LikeIcon') strokeVal = '#ff4554';
                    else if (iconKey.toLowerCase().includes('bookmark') || iconKey === 'BookmarkIcon') strokeVal = '#3b82f6';
                    else if (iconKey.toLowerCase().includes('bump') || iconKey === 'BumpIcon') strokeVal = '#10b981';
                    else if (iconKey.toLowerCase().includes('checkmark') || iconKey === 'CheckmarkIcon') strokeVal = '#8b5cf6';
                }

                // Remove attributes from props that aren't valid SVG attributes or let standard props override
                const { isLiked, isBookmarked, isBumped, isVouched, ...cleanProps } = props;

                return (
                    <span className="sticker-outline inline-flex items-center justify-center" style={{ display: 'inline-flex', verticalAlign: 'middle', ...stickerOutlineStyle, ...shadowStyle, ...props.style }}>
                        <svg
                            className={className}
                            viewBox={viewBox}
                            fill={fillVal}
                            stroke={strokeVal}
                            strokeWidth={strokeWidthVal}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            dangerouslySetInnerHTML={{ __html: innerHtml }}
                            {...cleanProps}
                            style={undefined}
                        />
                    </span>
                );
            }
        }

        if (DefaultComponent === DefaultGoodieWhiteIcon) {
            return <DefaultComponent {...props} />;
        }

        return (
            <span className="sticker-outline inline-flex items-center justify-center" style={{ display: 'inline-flex', verticalAlign: 'middle', ...stickerOutlineStyle, ...shadowStyle, ...props.style }}>
                <DefaultComponent {...props} style={undefined} />
            </span>
        );
    };
}

// ----------------------------------------------------
// DEFAULT ICONS DEFINITIONS
// ----------------------------------------------------

const DefaultLikeIcon = ({ className = 'w-6 h-6', isLiked = false, ...props }: IconProps) => (
    <svg className={className} viewBox="0 0 24 24" fill={isLiked ? '#ff4554' : 'none'} stroke={isLiked ? '#ff4554' : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
    </svg>
);

const DefaultBrokenHeartIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16.5 3c-1.74 0-3.41.81-4.5 2.09C10.91 3.81 9.24 3 7.5 3 4.42 3 2 5.42 2 8.5c0 3.78 6.6 2.98 12 13 .23.41.87.41 1.1 0 2.22-4.11 5.9-6.31 7.9-9.5C23 8.16 23 5.32 20.13 3.6A4.54 4.54 0 0 0 16.5 3z" fill="none" />
        <path d="M12 5.5l-2 4 4 2-2 4" />
    </svg>
);

const DefaultCommentIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
    </svg>
);

const DefaultCheckmarkIcon = ({ className = 'w-6 h-6', isVouched = false, ...props }: IconProps) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke={isVouched ? '#8b5cf6' : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
);

const DefaultBumpIcon = ({ className = 'w-6 h-6', isBumped = false, ...props }: IconProps) => (
    <svg className={className} viewBox="0 0 24 24" fill={isBumped ? '#10b981' : 'none'} stroke={isBumped ? '#10b981' : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <polyline points="17 1 21 5 17 9"></polyline><path d="M3 11V9a4 4 0 0 1 4-4h14"></path><polyline points="7 23 3 19 7 15"></polyline><path d="M21 13v2a4 4 0 0 1-4 4H3"></path>
    </svg>
);

const DefaultBookmarkIcon = ({ className = 'w-6 h-6', isBookmarked = false, ...props }: IconProps) => (
    <svg className={className} viewBox="0 0 24 24" fill={isBookmarked ? '#3b82f6' : 'none'} stroke={isBookmarked ? '#3b82f6' : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
    </svg>
);

const DefaultImageIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>
);

const DefaultVideoIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"></polygon><rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect></svg>
);

const DefaultAudioIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"></path><circle cx="6" cy="18" r="3"></circle><circle cx="18" cy="16" r="3"></circle></svg>
);

const DefaultMicIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path><path d="M19 10v2a7 7 0 0 1-14 0v-2"></path><line x1="12" y1="19" x2="12" y2="23"></line></svg>
);

const DefaultCloseIcon = ({ className = 'w-6 h-6', ...props }: IconProps) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
);

const DefaultPlayIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
);

const DefaultPauseIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg>
);

const DefaultTrashIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
);

const DefaultCameraIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>
);

const DefaultHomeIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
);

const DefaultUserIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
);

const DefaultUsersIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
);

const DefaultUserMinusIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><line x1="23" y1="11" x2="17" y2="11"></line></svg>
);

const DefaultMailIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
);

const DefaultSendIcon = ({ className = 'w-6 h-6', ...props }: IconProps) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" {...props} style={{ transform: 'rotate(-45deg) translateY(5%) translateX(15%)', ...props.style }}><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"></path></svg>
);

const DefaultSettingsIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
);

const DefaultEditIcon = ({ className = 'w-6 h-6', ...props }: IconProps) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
    </svg>
);

const DefaultExternalLinkIcon = ({ className = 'w-6 h-6' }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>;
const DefaultArrowUpIcon = ({ className = 'w-6 h-6' }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" /></svg>;

const DefaultGalleryIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" /></svg>
);

const DefaultPaperclipIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l7.07-7.07a4 4 0 00-5.656-5.657l-7.071 7.07a6 6 0 108.485 8.486L19 13" /></svg>
);

const DefaultChevronLeftIcon = ({ className = 'w-6 h-6' }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>;
const DefaultChevronRightIcon = ({ className = 'w-6 h-6' }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>;
const DefaultVolumeHighIcon = ({ className = 'w-5 h-5' }) => <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5 9v6h4l5 5V4L9 9H5z"></path></svg>;

const DefaultVolumeOffIcon = ({ className = 'w-5 h-5' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="11" fill="#FDDF6D" />
        <circle cx="12" cy="12" r="11" fill="#ecc884ff" opacity="0.4" style={{ mixBlendMode: 'multiply' }} />
        <circle cx="11" cy="10" r="2" fill="#36041dff" />
        <circle cx="18" cy="10" r="2" fill="#36041dff" />
    </svg>
);

const DefaultDownloadIcon = ({ className = 'w-5 h-5' }) => <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>;
const DefaultLogoutIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path></svg>
);
const DefaultSearchIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
);
const DefaultGoodieWhiteIcon = ({ className = 'w-6 h-6', ...props }: any) => (
    <img src="/assets/goodie-white.png" className={`${className} object-contain`} alt="" {...props} />
);
const DefaultReplyIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 17 4 12 9 7"></polyline><path d="M20 18v-2a4 4 0 0 0-4-4H4"></path></svg>
);
const DefaultForwardIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 17 20 12 15 7"></polyline><path d="M4 18v-2a4 4 0 0 1 4-4h12"></path></svg>
);
const DefaultAtIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"></circle><path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94"></path></svg>
);
const DefaultEmojiIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M8 14s1.5 2 4 2 4-2 4-2"></path><line x1="9" y1="9" x2="9.01" y2="9"></line><line x1="15" y1="9" x2="15.01" y2="9"></line></svg>
);
const DefaultMoreHorizontalIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"></circle><circle cx="19" cy="12" r="1"></circle><circle cx="5" cy="12" r="1"></circle></svg>
);
const DefaultPlusIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
);
const DefaultEyeIcon = ({ className = 'w-6 h-6', ...props }: IconProps) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
);
const DefaultEyeOffIcon = ({ className = 'w-6 h-6', ...props }: IconProps) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
    </svg>
);
const DefaultTrophyIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path><path d="M12 15c-2.209 0-4-1.791-4-4s1.791-4 4-4 4 1.791 4 4-1.791 4-4 4z"></path></svg>
);
const DefaultHashtagIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 20l4-16m2 16l4-16M5 9h14M5 15h14"></path></svg>
);
const DefaultGlobeIcon = ({ className = 'w-6 h-6', ...props }: IconProps) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>
);
const DefaultFilterIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
);
const DefaultXIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
);
const DefaultPrivateIcon = ({ className = 'w-6 h-6', ...props }: IconProps) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
);
const DefaultScreenShareIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
        <path d="M7 21h10" />
        <path d="M12 21v-4" />
        <path d="M12 11l3-3-3-3" />
        <path d="M15 8H9" />
    </svg>
);

const DefaultFlipCameraIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
    </svg>
);

const DefaultCopyIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
);

const DefaultPasteIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
        <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
    </svg>
);

const DefaultLockIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
);

const DefaultShieldIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
    </svg>
);

const DefaultSparklesIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707m12.728 0l-.707-.707M6.343 6.343l-.707-.707M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z" />
    </svg>
);

const DefaultFontIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="4 7 4 4 20 4 20 7" />
        <line x1="9" y1="20" x2="15" y2="20" />
        <line x1="12" y1="4" x2="12" y2="20" />
    </svg>
);
const DefaultPostcardIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="M16 8h2v2h-2z" />
        <path d="M4 12h8" />
        <path d="M4 16h6" />
    </svg>
);

const DefaultFireIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.653 11.75c-.322-1.564-1.29-2.91-2.613-3.702-.128-.077-.267-.145-.407-.21l-.096-.044c-1.397-.611-2.316-1.55-2.316-2.57 0-1.045.545-1.898 1.41-2.428-1.59-.06-3.132.55-4.225 1.63C8.315 5.52 7.7 7.042 7.7 8.65c0 1.25.352 2.417.962 3.398-1.748-1.025-3.082-.016-3.082 1.952 0 3.314 2.686 6 6 6s6-2.686 6-6c0-1.573-.61-3-1.61-4.06.28.093.582.14.883.14 1.657 0 3-1.343 3-3 0-1.04-.533-1.953-1.343-2.492.516.81.82 1.765.82 2.793 0 .285-.02.564-.06.84zM13 19c-2.21 0-4-1.79-4-4 0-.84.26-1.62.7-2.26.06.32.18.63.38.9.71.95 1.79 1.49 2.92 1.49 1.13 0 2.21-.54 2.92-1.49.2-.27.32-.58.38-.9.44.64.7 1.42.7 2.26 0 2.21-1.79 4-4 4z" />
    </svg>
);

const DefaultFlagIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"></path>
        <line x1="4" y1="22" x2="4" y2="15"></line>
    </svg>
);

const DefaultBellIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"></path>
        <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
    </svg>
);

const DefaultCalendarIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
);

const DefaultClockIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
);

const DefaultStoreIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
        <line x1="3" y1="6" x2="21" y2="6" />
        <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
);

const DefaultRotateIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
        <path d="M21 3v5h-5" />
    </svg>
);

const DefaultExpandIcon = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m15 15 6 6m-6-6v6m0-6h6M9 9 3 3m6 6V3m0 6H3" />
    </svg>
);



// ----------------------------------------------------
// EXPORTED WRAPPED DYNAMIC ICONS
// ----------------------------------------------------

export const LikeIcon = createDynamicIcon('LikeIcon', DefaultLikeIcon);
export const BrokenHeartIcon = createDynamicIcon('BrokenHeartIcon', DefaultBrokenHeartIcon);
export const CommentIcon = createDynamicIcon('CommentIcon', DefaultCommentIcon);
export const CheckmarkIcon = createDynamicIcon('CheckmarkIcon', DefaultCheckmarkIcon);
export const BumpIcon = createDynamicIcon('BumpIcon', DefaultBumpIcon);
export const BookmarkIcon = createDynamicIcon('BookmarkIcon', DefaultBookmarkIcon);
export const ImageIcon = createDynamicIcon('ImageIcon', DefaultImageIcon);
export const VideoIcon = createDynamicIcon('VideoIcon', DefaultVideoIcon);
export const AudioIcon = createDynamicIcon('AudioIcon', DefaultAudioIcon);
export const MicIcon = createDynamicIcon('MicIcon', DefaultMicIcon);
export const CloseIcon = createDynamicIcon('CloseIcon', DefaultCloseIcon);
export const PlayIcon = createDynamicIcon('PlayIcon', DefaultPlayIcon);
export const PauseIcon = createDynamicIcon('PauseIcon', DefaultPauseIcon);
export const TrashIcon = createDynamicIcon('TrashIcon', DefaultTrashIcon);
export const CameraIcon = createDynamicIcon('CameraIcon', DefaultCameraIcon);
export const HomeIcon = createDynamicIcon('HomeIcon', DefaultHomeIcon);
export const UserIcon = createDynamicIcon('UserIcon', DefaultUserIcon);
export const UsersIcon = createDynamicIcon('UsersIcon', DefaultUsersIcon);
export const MailIcon = createDynamicIcon('MailIcon', DefaultMailIcon);
export const SendIcon = createDynamicIcon('SendIcon', DefaultSendIcon);
export const SettingsIcon = createDynamicIcon('SettingsIcon', DefaultSettingsIcon);
export const EditIcon = createDynamicIcon('EditIcon', DefaultEditIcon);
export const ExternalLinkIcon = createDynamicIcon('ExternalLinkIcon', DefaultExternalLinkIcon);
export const ArrowUpIcon = createDynamicIcon('ArrowUpIcon', DefaultArrowUpIcon);
export const GalleryIcon = createDynamicIcon('GalleryIcon', DefaultGalleryIcon);
export const PaperclipIcon = createDynamicIcon('PaperclipIcon', DefaultPaperclipIcon);
export const ChevronLeftIcon = createDynamicIcon('ChevronLeftIcon', DefaultChevronLeftIcon);
export const ChevronRightIcon = createDynamicIcon('ChevronRightIcon', DefaultChevronRightIcon);
export const VolumeHighIcon = createDynamicIcon('VolumeHighIcon', DefaultVolumeHighIcon);
export const VolumeOffIcon = createDynamicIcon('VolumeOffIcon', DefaultVolumeOffIcon);
export const DownloadIcon = createDynamicIcon('DownloadIcon', DefaultDownloadIcon);
export const LogoutIcon = createDynamicIcon('LogoutIcon', DefaultLogoutIcon);
export const SearchIcon = createDynamicIcon('SearchIcon', DefaultSearchIcon);
export const GoodieWhiteIcon = createDynamicIcon('GoodieWhiteIcon', DefaultGoodieWhiteIcon);
export const ReplyIcon = createDynamicIcon('ReplyIcon', DefaultReplyIcon);
export const ForwardIcon = createDynamicIcon('ForwardIcon', DefaultForwardIcon);
export const AtIcon = createDynamicIcon('AtIcon', DefaultAtIcon);
export const EmojiIcon = createDynamicIcon('EmojiIcon', DefaultEmojiIcon);
export const MoreHorizontalIcon = createDynamicIcon('MoreHorizontalIcon', DefaultMoreHorizontalIcon);
export const PlusIcon = createDynamicIcon('PlusIcon', DefaultPlusIcon);
export const EyeIcon = createDynamicIcon('EyeIcon', DefaultEyeIcon);
export const EyeOffIcon = createDynamicIcon('EyeOffIcon', DefaultEyeOffIcon);
export const TrophyIcon = createDynamicIcon('TrophyIcon', DefaultTrophyIcon);
export const HashtagIcon = createDynamicIcon('HashtagIcon', DefaultHashtagIcon);
export const GlobeIcon = createDynamicIcon('GlobeIcon', DefaultGlobeIcon);
export const FilterIcon = createDynamicIcon('FilterIcon', DefaultFilterIcon);
const DefaultBrushIcon = ({ className = 'w-6 h-6', ...props }: IconProps) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5h2.586a1 1 0 01.707.293l8.158 8.158a2 2 0 11-2.828 2.828l-8.158-8.158A1 1 0 013 7.414V17a2 2 0 002 2h2z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5h2a2 2 0 012 2v2M19 13l-4 4" />
    </svg>
);

const DefaultShapeIcon = ({ className = 'w-6 h-6', ...props }: IconProps) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" />
    </svg>
);

export const XIcon = createDynamicIcon('XIcon', DefaultXIcon);
export const BrushIcon = createDynamicIcon('BrushIcon', DefaultBrushIcon);
export const ShapeIcon = createDynamicIcon('ShapeIcon', DefaultShapeIcon);
export const PrivateIcon = createDynamicIcon('PrivateIcon', DefaultPrivateIcon);
export const ScreenShareIcon = createDynamicIcon('ScreenShareIcon', DefaultScreenShareIcon);
export const FlipCameraIcon = createDynamicIcon('FlipCameraIcon', DefaultFlipCameraIcon);
export const CopyIcon = createDynamicIcon('CopyIcon', DefaultCopyIcon);
export const PasteIcon = createDynamicIcon('PasteIcon', DefaultPasteIcon);
export const LockIcon = createDynamicIcon('LockIcon', DefaultLockIcon);
export const ShieldIcon = createDynamicIcon('ShieldIcon', DefaultShieldIcon);
export const SparklesIcon = createDynamicIcon('SparklesIcon', DefaultSparklesIcon);
export const FontIcon = createDynamicIcon('FontIcon', DefaultFontIcon);
export const PostcardIcon = createDynamicIcon('PostcardIcon', DefaultPostcardIcon);
export const FireIcon = createDynamicIcon('FireIcon', DefaultFireIcon);
export const FlagIcon = createDynamicIcon('FlagIcon', DefaultFlagIcon);

export const CalendarIcon = createDynamicIcon('CalendarIcon', DefaultCalendarIcon);
export const ClockIcon = createDynamicIcon('ClockIcon', DefaultClockIcon);
export const StoreIcon = createDynamicIcon('StoreIcon', DefaultStoreIcon);
export const RotateIcon = createDynamicIcon('RotateIcon', DefaultRotateIcon);
export const ExpandIcon = createDynamicIcon('ExpandIcon', DefaultExpandIcon);
export const BellIcon = createDynamicIcon('BellIcon', DefaultBellIcon);

const DefaultPhoneIcon = ({ className = 'w-5 h-5', ...props }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
);
export const PhoneIcon = createDynamicIcon('PhoneIcon', DefaultPhoneIcon);
